
import { useCallback } from 'react';
import { Email } from '../types';
import { deleteEmail, moveEmailToFolder as apiMoveEmailToFolder, moveEmailToTrash } from '@/services/api/email';

interface UseEmailFolderOperationsProps {
  emails: Email[];
  setEmails: (emails: Email[]) => void;
}

export const useEmailFolderOperations = ({
  emails,
  setEmails,
}: UseEmailFolderOperationsProps) => {

  const moveToTrash = useCallback(async (id: string): Promise<Email | null> => {
    try {
      console.log('🗑️ useEmailFolderOperations.moveToTrash called with id:', id);
      console.log('🗑️ Current emails in state:', emails.map(e => ({ id: e.id, folder: e.folder })));
      
      // Find the email before moving it
      let emailToMove = emails.find(email => email.id === id);
      console.log('🗑️ Found email in emails list:', emailToMove);
      
      // If not found in emails list, we'll try to make the API call anyway
      // This handles the case where we're viewing an email detail that's not in the current folder list
      let currentFolder = 'inbox'; // default fallback
      
      if (emailToMove) {
        currentFolder = emailToMove.folder || 'inbox';
        console.log('🗑️ Using folder from found email:', currentFolder);
      } else {
        // Try to determine folder from current context or URL
        // Since we don't have the email in the list, we'll try common folders
        console.log('🗑️ Email not found in current list, will try API call with default folder');
        
        // Create a minimal email object for the API call
        emailToMove = {
          id,
          folder: currentFolder,
          // Add minimal required fields
          from: '',
          to: [],
          subject: '',
          date: new Date().toISOString(),
          read: true,
        } as Email;
      }
      
      console.log('🗑️ Current folder determined as:', currentFolder);
      
      // Use the new move to trash API endpoint
      console.log('🗑️ Calling moveEmailToTrash API...');
      const apiResult = await moveEmailToTrash(id, currentFolder);
      console.log('🗑️ API call completed, result:', apiResult);
      
      // Only remove from current emails list if the email was actually in the list
      if (emails.find(email => email.id === id)) {
        console.log('🗑️ Removing email from current list...');
        const updatedEmails = emails.filter(email => email.id !== id);
        setEmails(updatedEmails);
        console.log('🗑️ Updated emails list, new count:', updatedEmails.length);
      } else {
        console.log('🗑️ Email was not in current list, no need to remove');
      }
      
      // Return the moved email with updated folder
      const movedEmail = { ...emailToMove, folder: 'trash' };
      console.log('🗑️ Returning moved email:', movedEmail);
      return movedEmail;
    } catch (error) {
      console.error('💥 Failed to move email to trash:', error);
      throw error;
    }
  }, [emails, setEmails]);

  const moveToFolder = useCallback(async (id: string, targetFolder: string): Promise<Email | null> => {
    try {
      console.log(`📧 Moving email ${id} to ${targetFolder}`);
      // Find the email before moving it
      const emailToMove = emails.find(email => email.id === id);
      if (!emailToMove) return null;
      
      await apiMoveEmailToFolder(id, emailToMove.folder || 'inbox', targetFolder);
      // Remove from current emails list
      const updatedEmails = emails.filter(email => email.id !== id);
      setEmails(updatedEmails);
      
      // Return the moved email with updated folder
      return { ...emailToMove, folder: targetFolder };
    } catch (error) {
      console.error('💥 Failed to move email:', error);
      throw error;
    }
  }, [emails, setEmails]);

  const permanentlyDeleteEmail = useCallback(async (id: string): Promise<boolean> => {
    try {
      const email = emails.find(item => item.id === id);
      await deleteEmail(id, email?.folder || 'trash');
      const updatedEmails = emails.filter(email => email.id !== id);
      setEmails(updatedEmails);
      return true;
    } catch (error) {
      console.error('💥 Failed to permanently delete email:', error);
      throw error;
    }
  }, [emails, setEmails]);

  return {
    moveToTrash,
    moveToFolder,
    permanentlyDeleteEmail,
  };
};
